import { useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { useTenantMe, type TenantMe } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';

type EditableField = 'name' | 'contactEmail' | 'contactPhone' | 'vatNumber' | 'address' | 'brandingColor';

const FIELDS: { key: EditableField; label: string; type?: string }[] = [
  { key: 'name', label: 'Nom' },
  { key: 'contactEmail', label: 'Email de contact', type: 'email' },
  { key: 'contactPhone', label: 'Téléphone' },
  { key: 'vatNumber', label: 'N° de TVA' },
  { key: 'address', label: 'Adresse' },
  { key: 'brandingColor', label: 'Couleur (#RRGGBB)' },
];

// Colonnes nullables : vidées dans le formulaire, elles sont effacées (null). Nom et email de contact sont
// obligatoires : le navigateur bloque l'envoi s'ils sont vides (et, par sécurité, ils ne seraient pas envoyés).
const CLEARABLE = new Set<EditableField>(['contactPhone', 'vatNumber', 'address', 'brandingColor']);

export const toUpdateBody = (values: Record<EditableField, string>): Partial<Record<EditableField, string | null>> =>
  Object.fromEntries(
    Object.entries(values).flatMap(([key, v]): [string, string | null][] => {
      if (v.trim() !== '') return [[key, v]];
      return CLEARABLE.has(key as EditableField) ? [[key, null]] : [];
    }),
  );

const formOf = (t: TenantMe): Record<EditableField, string> =>
  Object.fromEntries(FIELDS.map(({ key }) => [key, t[key] ?? ''])) as Record<EditableField, string>;

/** Onglet « Tenant » des paramètres : lecture selon la projection reçue, modification et logo sous tenants.update. */
export function TenantSettingsCard() {
  const queryClient = useQueryClient();
  const { data: tenant } = useTenantMe();
  const canUpdate = useHasPermission('tenants.update');
  const [form, setForm] = useState<Record<EditableField, string> | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tenant-me'] });

  const save = useMutation({
    mutationFn: async (values: Record<EditableField, string>) => {
      await api.patch('/tenants/me/update', toUpdateBody(values));
    },
    onSuccess: async () => {
      toast.success('Tenant mis à jour');
      setForm(null);
      await refresh();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Mise à jour impossible')),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const data = new FormData();
      data.append('logo', file);
      await api.post('/tenants/me/branding/logo', data);
    },
    onSuccess: async () => {
      toast.success('Logo mis à jour');
      await refresh();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Envoi du logo impossible')),
  });

  const onLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadLogo.mutate(file);
    e.target.value = '';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Informations du tenant</CardTitle>
        {canUpdate && tenant && !form && (
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" /> {uploadLogo.isPending ? 'Envoi…' : 'Changer le logo'}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="sr-only" onChange={onLogo} aria-label="Logo du tenant" />
              </label>
            </Button>
            <Button onClick={() => setForm(formOf(tenant))}>
              <Pencil className="h-4 w-4 mr-2" /> Modifier le tenant
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {form ? (
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}
          >
            {FIELDS.map(({ key, label, type }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`tenant-${key}`}>{label}</Label>
                <Input
                  id={`tenant-${key}`}
                  type={type ?? 'text'}
                  value={form[key]}
                  required={!CLEARABLE.has(key)}
                  minLength={key === 'name' ? 2 : undefined}
                  placeholder={CLEARABLE.has(key) ? 'Laisser vide pour effacer' : undefined}
                  aria-describedby={CLEARABLE.has(key) ? `tenant-${key}-hint` : undefined}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
                {CLEARABLE.has(key) && form[key].trim() === '' && tenant?.[key] && (
                  <p id={`tenant-${key}-hint`} className="text-xs text-amber-700">
                    Sera effacé à l’enregistrement (actuellement : {tenant[key]})
                  </p>
                )}
              </div>
            ))}
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={save.isPending}>Enregistrer</Button>
              <Button type="button" variant="outline" onClick={() => setForm(null)}>Annuler</Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <dt className="text-muted-foreground">Nom</dt>
            <dd>{tenant?.name ?? '—'}</dd>
            <dt className="text-muted-foreground">Identifiant</dt>
            <dd className="font-mono">{tenant?.slug ?? '—'}</dd>
            {/* Champs présents seulement avec tenants.read (projection complète) */}
            {tenant?.contactEmail !== undefined && (
              <>
                <dt className="text-muted-foreground">Email de contact</dt>
                <dd>{tenant.contactEmail}</dd>
                <dt className="text-muted-foreground">Téléphone</dt>
                <dd>{tenant.contactPhone || '—'}</dd>
                <dt className="text-muted-foreground">N° de TVA</dt>
                <dd>{tenant.vatNumber || '—'}</dd>
                <dt className="text-muted-foreground">Adresse</dt>
                <dd>{tenant.address || '—'}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Couleur</dt>
            <dd>{tenant?.brandingColor ?? '—'}</dd>
            <dt className="text-muted-foreground">Logo</dt>
            <dd>
              {tenant?.brandingLogoUrl
                ? <img src={tenant.brandingLogoUrl} alt="Logo du tenant (aperçu)" className="h-12 max-w-[12rem] rounded border object-contain p-1" />
                : '—'}
            </dd>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
